"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Field, Input, Checkbox, FormStatus, SubmitButton } from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import styles from "./AvatarUploadModal.module.css";

/**
 * DeleteAccountModal — usunięcie konta przez 2-stage flow:
 *
 * **Stage "form":** RHF + zod form `{current_password, confirm_understood}`.
 *   - Re-auth obecnym hasłem (defense against session hijack)
 *   - Wymagany checkbox "rozumiem konsekwencje" (UX safety net)
 *
 * **Stage "sent":** info "wysłano email" po success init.
 *   - User widzi masked adres odbiorcy
 *   - Instrukcja: kliknij link POTWIERDŹ w emailu (1h expiry) lub ANULUJ
 *
 * Cały flow analogiczny do ChangeEmailModal ale prostszy (1 email z 2 linkami
 * zamiast 3-stage approve/confirm). Po finalnym confirm (klik z emaila) konto
 * jest usuwane + wszystkie sesje invalidated -> user wraca jako wylogowany.
 */
const deleteAccountSchema = z.object({
  current_password: z.string().min(1, "Podaj aktualne hasło."),
  confirm_understood: z
    .boolean()
    .refine((v) => v === true, "Wymagana jest świadoma zgoda na usunięcie konta."),
});

type DeleteAccountFormValues = z.infer<typeof deleteAccountSchema>;

export function DeleteAccountModal({
  currentEmail,
  onClose,
  onSuccess,
}: {
  currentEmail: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [stage, setStage] = useState<"form" | "sent">("form");
  const [topError, setTopError] = useState<string | null>(null);
  const [sentResult, setSentResult] = useState<InitDeleteAccountResponse | null>(null);

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<DeleteAccountFormValues>({
    resolver: zodResolver(deleteAccountSchema),
    mode: "onTouched",
    defaultValues: { current_password: "", confirm_understood: false },
  });

  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  /* Drag-from-input-to-backdrop guard (zob. ChangeEmailModal). */
  const backdropMouseDownTargetRef = useRef<EventTarget | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isSubmitting, onClose]);

  const onSubmit = async (values: DeleteAccountFormValues) => {
    setTopError(null);

    /* Custom URLs dla linków w emailu — backend podstawia `{token}` `{id_user}`
       placeholders. User wraca na `/konto?action=delete-account&...` lub
       `/konto?action=cancel-delete-account&...`. */
    const origin = window.location.origin;
    const pathname = window.location.pathname;
    const customDeleteUrl = `${origin}${pathname}?action=delete-account&token={token}&user_id={id_user}`;
    const customCancelUrl = `${origin}${pathname}?action=cancel-delete-account&token={token}&user_id={id_user}`;

    try {
      const res = await fetch("/api/iiapi/user/delete-account/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: values.current_password,
          custom_delete_url: customDeleteUrl,
          custom_cancel_url: customCancelUrl,
        }),
      });
      const body = (await res.json().catch(() => null)) as
        | (InitDeleteAccountResponse & { ok?: boolean; message?: string; code?: number })
        | null;

      if (!res.ok || !body?.ok) {
        const code = body?.code;
        /* 2120173 = nieprawidlowe haslo (initDeleteAccount, dawny kod 2004 - przenumerowane
           przy standaryzacji IERROR::getCode, zakres metody 2120170-2120177). */
        if (code === 2120173) {
          setError("current_password", {
            type: "server",
            message: body?.message ?? "Nieprawidłowe hasło.",
          });
          return;
        }
        setTopError(body?.message ?? "Nie udało się zainicjować usunięcia konta.");
        return;
      }

      setSentResult(body);
      setStage("sent");
    } catch (e) {
      setTopError(e instanceof Error ? `Błąd sieci: ${e.message}` : "Błąd sieci.");
    }
  };

  if (!mounted) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        backdropMouseDownTargetRef.current = e.target;
      }}
      onClick={(e) => {
        if (
          e.target === e.currentTarget &&
          backdropMouseDownTargetRef.current === e.currentTarget &&
          !isSubmitting
        ) {
          onClose();
        }
        backdropMouseDownTargetRef.current = null;
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Usunięcie konta"
    >
      <div className={styles.modal}>
        <header className={styles.header}>
          <h2 className={styles.title}>
            {stage === "form" ? "Usuń konto" : "Sprawdź skrzynkę"}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
          >
            <Icon name="x" />
          </button>
        </header>

        <div className={styles.body}>
          {isSubmitting && (
            <div className={styles.loadingOverlay} aria-busy="true" role="status">
              <div className={styles.spinner} aria-hidden="true" />
              <p className={styles.loadingText}>Wysyłanie emaila potwierdzającego…</p>
            </div>
          )}

          {stage === "form" && (
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              style={{ display: "flex", flexDirection: "column", gap: "var(--space-md)" }}
            >
              <FormStatus variant="warning">
                <strong>Uwaga — operacja nieodwracalna!</strong>
                <br />Po potwierdzeniu Twoje konto zostanie usunięte, a wszystkie sesje
                na innych urządzeniach automatycznie wylogowane.
                <br /><br />
                Procedura jest 2-etapowa: po wpisaniu hasła wyślemy email z linkiem
                <strong> POTWIERDŹ USUNIĘCIE</strong> oraz <strong>ANULUJ</strong>.
                Konto zostanie usunięte dopiero po kliknięciu linku w mailu (1h ważności).
              </FormStatus>

              <p className={styles.loadingText} style={{ textAlign: "left" }}>
                Email konta: <strong>{currentEmail}</strong>
              </p>

              <Field
                label="Aktualne hasło"
                required
                error={errors.current_password?.message}
              >
                <Input
                  type="password"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  {...register("current_password")}
                />
              </Field>

              <Controller
                name="confirm_understood"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    checked={field.value === true}
                    onChange={(e) => field.onChange(e.target.checked)}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                    disabled={isSubmitting}
                    error={
                      typeof errors.confirm_understood?.message === "string"
                        ? errors.confirm_understood.message
                        : undefined
                    }
                  >
                    Rozumiem, że usunięcie konta jest nieodwracalne i tracę dostęp do
                    wszystkich danych zapisanych pod tym kontem.
                  </Checkbox>
                )}
              />

              {topError && <FormStatus variant="error">{topError}</FormStatus>}

              <div className={styles.footer} style={{ marginTop: "var(--space-sm)" }}>
                <button
                  type="button"
                  className={styles.linkBtn}
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Anuluj
                </button>
                <SubmitButton loading={isSubmitting}>Wyślij email potwierdzający</SubmitButton>
              </div>
            </form>
          )}

          {stage === "sent" && sentResult && (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-md)" }}>
              <FormStatus variant="success">
                Wysłano email na Twój adres.
              </FormStatus>

              <div>
                <p
                  className={styles.loadingText}
                  style={{
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Icon name="envelope" aria-hidden />
                  <span>
                    Email z linkiem POTWIERDŹ + ANULUJ wysłany na:{" "}
                    <strong>{sentResult.email_masked}</strong>
                  </span>
                  {!sentResult.notifications.warning.sent && (
                    <>
                      <Icon name="x-circle" aria-hidden />
                      <span>(nie udało się wysłać)</span>
                    </>
                  )}
                </p>
              </div>

              <FormStatus variant="warning">
                <strong>Co dalej?</strong>
                <br />1. Otwórz skrzynkę (sprawdź też folder <strong>SPAM</strong> /
                wiadomości niechciane).
                <br />2. Kliknij <strong>„POTWIERDŹ USUNIĘCIE"</strong> aby ostatecznie
                usunąć konto, lub <strong>„ANULUJ"</strong> jeśli zmieniłeś zdanie /
                ktoś inny zainicjował operację.
                <br />3. Link wygasa za <strong>1 godzinę</strong>. Po tym czasie
                procedura automatycznie zostanie anulowana.
              </FormStatus>

              <div className={styles.footer} style={{ marginTop: "var(--space-sm)" }}>
                <SubmitButton onClick={onSuccess}>Rozumiem, zamknij</SubmitButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

type InitDeleteAccountResponse = {
  state: "pending_confirmation";
  expires_in: number;
  expiry_date: string;
  email_masked: string;
  notifications: {
    warning: { sent: boolean; to_masked: string };
  };
};
